import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, switchMap } from 'rxjs';
import { UserProfileService } from '../services/user-profile.service';
import type { UserProfile } from '../models';

/**
 * UserProfileStore
 *
 * Page-scoped store for the merchant user profile
 * (POST /api/gozem_money/user_profile). Provide it via the consuming page's
 * `providers` (e.g. WalletPage) — NOT `providedIn: 'root'`.
 *
 * Single responsibility: expose the merchant `profile` and the derived
 * suspension state that drives the persistent suspension banner.
 *
 * Why page-scoped: the load fires in the constructor, so the store must be
 * constructed only when the consuming page mounts (user authenticated, storage
 * ready). A root singleton would be constructed at first injection anywhere in
 * the DI graph — e.g. on the login page — firing the request before auth and
 * never refetching on later navigation. Mirrors WalletStore.
 *
 * The load pipeline uses Subject + switchMap so a refresh() cancels any
 * in-flight request. The instance is destroyed on navigation away, and
 * takeUntilDestroyed tears down the subscription — no cross-session leak.
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable()
export class UserProfileStore {
  private readonly userProfileService = inject(UserProfileService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Load pipeline ───────────────────────────────────────────────────────
  private readonly loadRequest$ = new Subject<void>();

  // ── Private mutable state ─────────────────────────────────────────────────
  private readonly _profile = signal<UserProfile | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  // ── Public readonly state ─────────────────────────────────────────────────
  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // ── Derived suspension state ──────────────────────────────────────────────
  readonly isSuspended = computed(() => this._profile()?.status === 'suspended');
  readonly suspensionReason = computed(() => this._profile()?.suspension_reason ?? '');
  readonly suspensionReasonCode = computed(() => this._profile()?.suspension_reason_code ?? '');

  constructor() {
    this.loadRequest$
      .pipe(
        switchMap(() => {
          this._loading.set(true);
          this._error.set(null);
          return this.userProfileService.getUserProfile().pipe(
            catchError(() => {
              this._error.set('Impossible de charger le profil.');
              this._loading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response.success) {
          this._profile.set(response.response.profile);
        } else {
          this._error.set('Impossible de charger le profil.');
        }
        this._loading.set(false);
      });

    // Fire the initial load on first injection.
    this.loadRequest$.next();
  }

  /** Cancels any in-flight request and re-fetches the profile. */
  refresh(): void {
    this.loadRequest$.next();
  }
}
